import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Loader2,
  Lock,
  Eye,
  EyeOff,
  CheckCircle,
  AlertTriangle,
  LogOut,
  Key,
  Shield,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useAuthStore } from '@/store/auth.store';
import { vaultService } from '@/services/vault.service';
import { initializeVault } from '@/utils/crypto/vaultCrypto';
import { useVaultKeyStore } from '@/store/vaultKeyStore';
import { VaultUserLayout } from '../components';

const initializeSchema = z
  .object({
    password: z.string().min(1, 'Mot de passe requis'),
    confirmPassword: z.string().min(1, 'Confirmation requise'),
  })
  .refine(data => data.password === data.confirmPassword, {
    message: 'Les mots de passe ne correspondent pas',
    path: ['confirmPassword'],
  });

type FormData = z.infer<typeof initializeSchema>;

/**
 * VaultInitializePage
 *
 * Page for vault users to initialize their vault with zero-knowledge encryption.
 * The user provides their password which is used to derive the encryption key
 * client-side. The server never sees the plaintext password or key.
 */
export const VaultInitializePage = () => {
  const { t } = useTranslation();
  const { vaultId } = useParams<{ vaultId: string }>();
  const navigate = useNavigate();
  const { user, logout, setUser } = useAuthStore();
  const unlockVault = useVaultKeyStore(state => state.unlockVault);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [initializationStep, setInitializationStep] = useState<
    'input' | 'deriving' | 'encrypting' | 'saving' | 'complete'
  >('input');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(initializeSchema),
  });

  // Fetch pending initialization status
  const {
    data: pendingData,
    isLoading: isPendingLoading,
    error: pendingError,
  } = useQuery({
    queryKey: ['pending-initialization', vaultId],
    queryFn: () => vaultService.getPendingInitialization(vaultId!),
    enabled: !!vaultId,
  });

  // Initialize vault mutation
  const initializeMutation = useMutation({
    mutationFn: async (password: string) => {
      if (!vaultId) throw new Error('Vault ID is required');

      // Step 1: Deriving key from password (client-side)
      setInitializationStep('deriving');
      const { masterKey, initData } = await initializeVault(password);

      // Step 2: Encrypting vault keys
      setInitializationStep('encrypting');
      // Already done in initializeVault

      // Step 3: Saving to server
      setInitializationStep('saving');
      const response = await vaultService.userInitializeVault(
        vaultId,
        initData
      );

      // Step 4: Store master key in session
      unlockVault(vaultId, masterKey);

      return { vault: response.data, masterKey };
    },
    onSuccess: () => {
      setInitializationStep('complete');
      toast.success(
        t('vault.initialize.success', 'Coffre-fort initialisé avec succès')
      );

      // Update user state to clear pendingVaultId
      if (user) {
        setUser({
          ...user,
          pendingVaultId: null,
        });
      }

      // Navigate to my-vaults after a short delay
      setTimeout(() => {
        navigate('/my-vaults');
      }, 1500);
    },
    onError: (error: HttpApiError) => {
      setInitializationStep('input');
      const message =
        error?.response?.data?.message ||
        error?.message ||
        t('vault.initialize.error', "Erreur lors de l'initialisation");
      toast.error(message);
    },
  });

  const onSubmit = async (data: FormData) => {
    initializeMutation.mutate(data.password);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Redirect if vault is already initialized
  useEffect(() => {
    if (pendingData && !pendingData.data.isPending) {
      toast.info(
        t(
          'vault.initialize.alreadyInitialized',
          'Ce coffre-fort est déjà initialisé'
        )
      );
      navigate('/my-vaults');
    }
  }, [pendingData, navigate, t]);

  // Loading state
  if (isPendingLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="text-primary-600 h-10 w-10 animate-spin" />
          <p className="text-sm text-neutral-600">
            {t('vault.initialize.loading', 'Chargement...')}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (pendingError || !vaultId) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4">
        <div className="w-full max-w-md rounded-xl border border-neutral-200 bg-white p-8">
          <div className="flex flex-col items-center text-center">
            <div className="bg-error-50 mb-4 flex h-12 w-12 items-center justify-center rounded-full">
              <AlertTriangle className="text-error-600 h-6 w-6" />
            </div>
            <h1 className="mb-2 text-xl font-semibold text-neutral-900">
              {t('vault.initialize.errorTitle', 'Erreur')}
            </h1>
            <p className="mb-6 text-sm text-neutral-600">
              {t(
                'vault.initialize.errorDescription',
                'Impossible de charger les informations du coffre-fort.'
              )}
            </p>
            <button
              onClick={handleLogout}
              className="bg-primary-600 hover:bg-primary-700 flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium text-white transition-colors"
            >
              <LogOut className="h-4 w-4" />
              {t('common.logout', 'Déconnexion')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const vault = pendingData?.data.vault;

  return (
    <VaultUserLayout
      title={t('vault.initialize.pageTitle', 'Initialisation du coffre-fort')}
    >
      <div className="flex flex-1 items-center justify-center">
        <div className="w-full max-w-lg rounded-xl border border-neutral-200 bg-white">
          {/* Header */}
          <div className="border-b border-neutral-200 p-6 text-center">
            <div className="bg-primary-100 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
              <Key className="text-primary-600 h-8 w-8" />
            </div>
            <h2 className="text-xl font-bold text-neutral-900">
              {t('vault.initialize.title', 'Sécurisez votre coffre-fort')}
            </h2>
            {vault?.name && (
              <p className="mt-2 text-sm text-neutral-600">
                {t('vault.initialize.vaultName', 'Coffre-fort :')} {vault.name}
              </p>
            )}
          </div>

          {/* Progress Steps (when initializing) */}
          {initializationStep !== 'input' &&
            initializationStep !== 'complete' && (
              <div className="border-b border-neutral-200 bg-neutral-50 p-4">
                <div className="flex items-center justify-center gap-3">
                  <Loader2 className="text-primary-600 h-5 w-5 animate-spin" />
                  <span className="text-sm font-medium text-neutral-700">
                    {initializationStep === 'deriving' &&
                      t(
                        'vault.initialize.step.deriving',
                        'Dérivation de la clé de chiffrement...'
                      )}
                    {initializationStep === 'encrypting' &&
                      t(
                        'vault.initialize.step.encrypting',
                        'Chiffrement des clés...'
                      )}
                    {initializationStep === 'saving' &&
                      t(
                        'vault.initialize.step.saving',
                        'Sauvegarde sécurisée...'
                      )}
                  </span>
                </div>
              </div>
            )}

          {/* Success State */}
          {initializationStep === 'complete' && (
            <div className="p-8 text-center">
              <div className="bg-success-100 mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full">
                <CheckCircle className="text-success-600 h-8 w-8" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-neutral-900">
                {t('vault.initialize.complete.title', 'Coffre-fort sécurisé !')}
              </h3>
              <p className="text-sm text-neutral-600">
                {t(
                  'vault.initialize.complete.description',
                  'Votre coffre-fort est maintenant protégé par chiffrement de bout en bout.'
                )}
              </p>
            </div>
          )}

          {/* Form */}
          {initializationStep === 'input' && (
            <form onSubmit={handleSubmit(onSubmit)} className="p-6">
              <p className="mb-6 text-sm text-neutral-600">
                {t(
                  'vault.initialize.description',
                  "Choisissez un mot de passe pour sécuriser votre coffre-fort. Ce mot de passe sera utilisé pour chiffrer vos documents. Il n'est jamais stocké ni transmis - seul vous pouvez déverrouiller votre coffre."
                )}
              </p>

              <div className="space-y-4">
                {/* Password field */}
                <div>
                  <label
                    htmlFor="password"
                    className="mb-1 block text-xs font-medium tracking-wide text-neutral-500 uppercase"
                  >
                    {t(
                      'vault.initialize.form.password',
                      'Mot de passe du coffre'
                    )}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-4 w-4 text-neutral-400" />
                    </div>
                    <input
                      {...register('password')}
                      type={showPassword ? 'text' : 'password'}
                      id="password"
                      placeholder={t(
                        'vault.initialize.form.passwordPlaceholder',
                        'Entrez votre mot de passe'
                      )}
                      className="focus:border-primary-500 focus:ring-primary-500 block w-full rounded-lg border border-neutral-300 py-2.5 pr-10 pl-9 text-sm focus:ring-1 focus:outline-none"
                      disabled={initializeMutation.isPending}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-neutral-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-neutral-400" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-error-600 mt-1 text-xs">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                {/* Confirm Password field */}
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="mb-1 block text-xs font-medium tracking-wide text-neutral-500 uppercase"
                  >
                    {t(
                      'vault.initialize.form.confirmPassword',
                      'Confirmez le mot de passe'
                    )}
                  </label>
                  <div className="relative">
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                      <Lock className="h-4 w-4 text-neutral-400" />
                    </div>
                    <input
                      {...register('confirmPassword')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      placeholder={t(
                        'vault.initialize.form.confirmPasswordPlaceholder',
                        'Confirmez votre mot de passe'
                      )}
                      className="focus:border-primary-500 focus:ring-primary-500 block w-full rounded-lg border border-neutral-300 py-2.5 pr-10 pl-9 text-sm focus:ring-1 focus:outline-none"
                      disabled={initializeMutation.isPending}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute inset-y-0 right-0 flex items-center pr-3"
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4 text-neutral-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-neutral-400" />
                      )}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-error-600 mt-1 text-xs">
                      {errors.confirmPassword.message}
                    </p>
                  )}
                </div>

                {/* Submit button */}
                <button
                  type="submit"
                  disabled={initializeMutation.isPending}
                  className="bg-primary-600 hover:bg-primary-700 mt-4 flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:opacity-50"
                >
                  {initializeMutation.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {t(
                        'vault.initialize.form.initializing',
                        'Initialisation...'
                      )}
                    </>
                  ) : (
                    <>
                      <Shield className="h-4 w-4" />
                      {t(
                        'vault.initialize.form.submit',
                        'Sécuriser mon coffre-fort'
                      )}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Footer - Security note */}
          <div className="border-t border-neutral-200 bg-neutral-50 px-6 py-4">
            <div className="flex items-start gap-3 text-xs text-neutral-600">
              <Lock className="mt-0.5 h-4 w-4 flex-shrink-0 text-neutral-400" />
              <p>
                {t(
                  'vault.initialize.securityNote',
                  "Votre mot de passe n'est jamais envoyé à nos serveurs. Il est utilisé localement pour dériver une clé de chiffrement unique. Vos documents sont chiffrés de bout en bout."
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </VaultUserLayout>
  );
};
